import { AlertFactory } from 'alerts/Alert'
import BillingStore from 'billing/BillingStore'
import BillingMethod from 'billing/models/BillingMethod'
import PaymentMethodsRootViewModel from 'billing/payment-methods/PaymentMethodsRootViewModel'
import Member from 'iam/models/Member'
import Team from 'iam/models/Team'
import TeamMember from 'iam/models/TeamMember'
import { GetCurrentMember } from 'iam/SessionStore'
import TeamMemberStore from 'iam/TeamMemberStore'
import WorkspaceMembershipStore from 'iam/WorkspaceMembershipStore'
import { CancellationToken } from 'infra/cancellation'
import { FindLegendsQuery } from 'legends/dtos/legend'
import { LegendVersionId, PublishedLegend } from 'legends/PublishedLegend'
import PublishedLegendStore from 'legends/PublishedLegendStore'
import { Shortcuts } from 'shortcuts/Shortcuts'
import { CursorPaged, Id } from 'types/definitions'
import CouponStore from '../billing/CouponStore'
import Coupon from '../billing/models/Coupon'
import DocumentStore from '../documents/DocumentStore'
import {
  DraftChange,
  RequestedProviderSelection,
  TeamSelection,
} from './creation/DraftChange'
import { JobCreationPorts } from './creation/ports'
import Job from './Job'
import { JobDocumentsLinkingAdapter } from './JobDocumentsLinkingAdapter'
import JobStore from './JobStore'
import ProviderStore from './ProviderStore'
import QuestionListViewModel from './questions/QuestionListViewModel'
import QuestionStore, { QuestionsQuerySession } from './QuestionStore'
import Summary from './Summary'
import SummaryStore from './SummaryStore'
import MemberStore from 'iam/MemberStore'
import { FindMembersQuery } from 'iam/dtos/member'
import { UserDataType } from 'iam/models/UserDataType'

/**
 * This adapter implements the functionality that Job Creation needs from other areas
 * of the system. This lets us reason about those dependencies.
 */
export default class JobCreationAdapter implements JobCreationPorts {
  requestRename$ = this.shortcuts.rename$

  constructor(
    public getCurrentMember: GetCurrentMember,
    private workspaceMembershipStore: WorkspaceMembershipStore,
    private memberStore: MemberStore,
    private teamMemberStore: TeamMemberStore,
    private jobStore: JobStore,
    private providerStore: ProviderStore,
    private publishedLegendStore: PublishedLegendStore,
    private questionStore: QuestionStore,
    private summaryStore: SummaryStore,
    private documentStore: DocumentStore,
    private couponStore: CouponStore,
    private billingStore: BillingStore,
    private paymentMethodsRoot: PaymentMethodsRootViewModel,
    public newAlert: AlertFactory,
    private shortcuts: Shortcuts,
  ) {}

  fetchMember = (workspaceId: Id, userPublicId: Id) => {
    return this.memberStore.fetchByUserPublicId({
      workspace_id: Id.assert(workspaceId),
      user_public_id: Id.assert(userPublicId),
    })
  }

  fetchBillingProfile = async (member: Member, team: TeamSelection) => {
    return team === TeamSelection.myself
      ? this.billingStore.fetchBillingMethodsForIndividual(member)
      : this.billingStore.fetchBillingMethodsForTeam(team)
  }

  canChangeBillingMethod(member: Member, team: TeamSelection): boolean {
    return team === TeamSelection.myself
      ? Boolean(
          this.billingStore.individualBillingProfile(member)?.allowedToManage,
        )
      : Boolean(this.billingStore.teamBillingProfile(team)?.allowedToManage)
  }

  billingMethodsFor = (
    member: Member,
    team: TeamSelection,
  ): BillingMethod[] => {
    return team === TeamSelection.myself
      ? this.billingStore.billingMethodsForIndividual(member)
      : this.billingStore.billingMethodsForTeam(team)
  }

  saveSelectedBillingMethod = async (
    job: Job,
    billingMethod: BillingMethod,
  ) => {
    await this.billingStore.changeDefaultBillingMethod(
      billingMethod,
      job.ownerTeam,
    )
  }

  selectBillingMethod = (
    member: Member,
    team: TeamSelection,
    currentBillingMethod: BillingMethod,
    onSave: (billingMethod: BillingMethod) => Promise<unknown>,
    ct: CancellationToken,
  ): Promise<BillingMethod | null> => {
    return this.paymentMethodsRoot.selectPaymentMethod(
      member,
      team === TeamSelection.myself ? null : team,
      currentBillingMethod,
      onSave,
      ct,
    )
  }

  newBillingMethodFor = async (
    member: Member,
    team: TeamSelection,
    ct: CancellationToken,
  ): Promise<BillingMethod | null> => {
    return this.paymentMethodsRoot.newPaymentMethodModal(
      member,
      team === TeamSelection.myself ? null : team,
      ct,
    )
  }

  getAsOfDate = (job: Job) => {
    return job === Job.none ? new Date() : job.dateCreated
  }

  fetchSummary = async (
    job: Job,
    legend: PublishedLegend,
  ): Promise<Summary> => {
    return this.summaryStore.fetchSummary(job, legend)
  }

  draft = async (changes: DraftChange[]): Promise<Job | null> => {
    const [params, otherChanges] = parseDraftParams(changes)
    if (!params) {
      return null
    }

    try {
      const job = params.teamId
        ? await this.jobStore.createTeamDraftJob(
            params.legendVersionId,
            params.teamId,
            params.ownerId,
          )
        : await this.jobStore.createDraftJob(
            params.legendVersionId,
            params.ownerId,
          )

      await this.persistChanges(job, otherChanges)
      return job
    } catch (err) {
      console.error('Error trying to create draft', err)
      throw err
    }
  }

  createDraftJob = async (
    legendVersionId: LegendVersionId,
    clientUserPublicId: Id,
  ): Promise<Job> => {
    try {
      const job = await this.jobStore.createDraftJob(
        legendVersionId,
        clientUserPublicId,
      )

      return job
    } catch (err) {
      console.error('Error trying to create draft', err)
      throw err
    }
  }

  createTeamDraftJob = async (
    legendVersionId: LegendVersionId,
    teamId: Id,
    clientUserPublicId: Id,
  ): Promise<Job> => {
    try {
      const job = await this.jobStore.createTeamDraftJob(
        legendVersionId,
        teamId,
        clientUserPublicId,
      )

      return job
    } catch (err) {
      console.error('Error trying to create team draft', err)
      throw err
    }
  }

  persistChanges = async (
    job: Job,
    changes: readonly DraftChange[],
  ): Promise<DraftChange[]> => {
    try {
      const persistedChanges: DraftChange[] = []
      for (const change of changes) {
        switch (change.type) {
          case 'LegendSelected': {
            if (change.legend) {
              await this.jobStore.updateDraftLegendVersion(job.id)
              persistedChanges.push(change)
            }
            break
          }
          case 'SupportingClientAdded': {
            await this.jobStore.addSupportingClientAsAdmin(
              Id.assert(job.id),
              Id.assert(change.member.user.publicId),
              'Editing draft',
            )
            persistedChanges.push(change)
            break
          }
          case 'SupportingClientRemoved': {
            await this.jobStore.removeCollaboratorAsAdmin(
              Id.assert(job.id),
              Id.assert(change.member.user.publicId),
              'Editing draft',
            )
            persistedChanges.push(change)
            break
          }
          case 'AnswersChanged':
            await this.questionStore.replaceQuestionAnswers(
              job.id,
              change.answers,
            )
            persistedChanges.push(change)

            break
          case 'Renamed':
            await this.rename(job, change.newName)
            persistedChanges.push(change)
            break
          case 'RequestedProviderChanged':
            if (change.requestedProvider) {
              const requestedProId =
                change.requestedProvider ===
                RequestedProviderSelection.firstAvailable
                  ? null
                  : change.requestedProvider.user.publicId

              await this.requestChampionProvider(job, requestedProId)

              persistedChanges.push(change)
            }

            break
          case 'CouponSelected':
            await this.jobStore.applyCoupon(job, change.coupon.id)
            persistedChanges.push(change)
            break
          case 'CouponCleared':
            await this.jobStore.removeCoupon(job)
            persistedChanges.push(change)
            break
          case 'BillingMethodSelected':
            await this.saveSelectedBillingMethod(job, change.billingMethod)
            persistedChanges.push(change)
            break
          default:
            // Workspace and Team changes should not be possible
            // after having drafted the job.
            console.warn(`Unhandled change type: ${change.type}`, change)
        }
      }

      return persistedChanges
    } catch (err) {
      console.error('Error trying to persist changes:', err)
      throw err
    }
  }

  requestChampionProvider = async (job: Job, requestedProId: Id | null) => {
    return this.jobStore.requestChampionProvider(job, requestedProId)
  }

  rename = async (job: Job, newName: string) => {
    return this.jobStore.rename(job, newName)
  }

  submitRequest = async (
    job: Job,
    paymentMethod: BillingMethod | null,
    bypassBilling?: boolean,
  ): Promise<void> => {
    await this.jobStore.submitRequestV3(job.id, {
      paymentMethod,
      bypassBilling,
    })
  }

  fetchJob = async (jobId: Id): Promise<Job> => {
    const job = await this.jobStore.fetchJob(Id.assert(jobId))
    return job || Job.none
  }

  fetchPreviousProvidersForMember = async (
    member: Member,
  ): Promise<Member[]> => {
    return this.providerStore.fetchAvailableProvidersForMember(member)
  }

  deleteDraft = async (job: Job): Promise<void> => {
    await this.jobStore.deleteDraftAsCollaborator(job)
  }

  fetchUserTeams = async (_workspaceId: string) => {
    // Workspace ID isn't used here since we fetch all teams for the user.
    await this.teamMemberStore.fetchUserTeamMembers({
      user_id: this.getCurrentMember().user.publicId,
      include_team: true,
    })
  }

  fetchUserTeamsByUserPublicId = async (
    _workspaceId: string,
    userPublicId: Id,
  ) => {
    await this.teamMemberStore.fetchUserTeamMembersForWorkspace({
      user_id: userPublicId,
      workspace_id: _workspaceId,
      include_team: true,
    })
  }

  getTeam = (teamId: Id): Team => {
    return (
      this.teamMemberStore.getTeamMember(
        teamId,
        this.getCurrentMember().user.publicId,
      )?.team ?? Team.none
    )
  }

  findAvailableLegendVersions = (
    query: FindLegendsQuery,
  ): Promise<CursorPaged<PublishedLegend>> => {
    return this.publishedLegendStore.findLegends(query)
  }

  fetchLatestLegendVersion = (legendId: Id): Promise<PublishedLegend> => {
    return this.publishedLegendStore.fetchLatestLegendVersion(legendId)
  }

  fetchLegendVersion = (
    legendVersionId: LegendVersionId,
  ): Promise<PublishedLegend> => {
    return this.publishedLegendStore.fetchLegendVersion(legendVersionId)
  }

  getPublishedLegend = (legendVersionId: LegendVersionId): PublishedLegend => {
    return this.publishedLegendStore.get(legendVersionId)
  }

  publishedLegendsForWorkspace = (workspaceId: Id): PublishedLegend[] => {
    return this.publishedLegendStore.forWorkspace(workspaceId)
  }

  fetchAvailableWorkspaceMembers = async (): Promise<void> => {
    await this.workspaceMembershipStore.fetchWorkspaceMemberships()
  }

  fetchAvailableMembersForWorkspace = async (
    query: FindMembersQuery,
    ct = CancellationToken.default,
  ): Promise<CursorPaged<Member>> => this.memberStore.search(query, ct)

  fetchAvailableClientMembersForWorkspace = async (
    query: FindMembersQuery,
    ct = CancellationToken.default,
  ): Promise<CursorPaged<Member>> =>
    this.memberStore.search(
      { user_types: [UserDataType.User, UserDataType.Admin], ...query },
      ct,
    )

  availableWorkspaceMembers = (): Member[] => {
    // Only show workspaces where either:
    // - the user can create jobs at the workspace level
    // - the user can create jobs at the team level
    return this.workspaceMembershipStore
      .getCurrentUserWorkspaceMemberships()
      .filter((m) => {
        if (m.member.hasSomePermissions('JOB_CREATE', 'ADMIN_JOB_CREATE')) {
          return true
        }

        return Boolean(
          this.teamMembersWithCreateAccess(
            this.getCurrentMember().user.id,
            m.workspace.id,
          ).length,
        )
      })
      .map((m) => m.member)
  }

  availableMembersForWorkspace = (workspaceId: Id): Member[] => {
    return this.memberStore.forWorkspace(workspaceId)
  }

  getAvailableTeamMembersByUserAndWorkspace = (
    userId: Id,
    workspaceId: Id,
  ): TeamMember[] => {
    return this.teamMembersWithCreateAccess(userId, workspaceId)
  }

  createQuestionListViewModel = (job: Job): QuestionListViewModel => {
    const docsAdapter = new JobDocumentsLinkingAdapter(
      job,
      this.documentStore,
      this.getCurrentMember,
    )
    return new QuestionListViewModel({
      documentRequestsAdapterFactory: () => docsAdapter.documentRequestsAdapter,
      getAsOfDate: () => job.dateCreated,
    })
  }

  createQuestionsQuerySession = (job: Job): QuestionsQuerySession => {
    return this.questionStore.createSessionForJob(job.id)
  }

  addCouponToMember = async (
    member: Member,
    code: string,
  ): Promise<Coupon | null> => {
    return this.couponStore
      .addCouponToUser(member, code)
      .then((u) => u?.coupon ?? null)
  }

  couponsForMember = (member: Member): Coupon[] => {
    return this.couponStore
      .couponsForMember(member)
      .filter((uc) => !uc.redeemed)
      .map((c) => c.coupon)
  }

  fetchCouponsForMember = async (member: Member): Promise<void> => {
    await this.couponStore.fetchUserCoupons(member)
  }

  fetchCoupon = (couponId: string): Promise<Coupon> => {
    return this.couponStore.fetchCoupon(couponId)
  }

  copyDraft = async (job: Job) => this.jobStore.copyJob(job)

  /**
   * Gets team members with create access in teams that the user is a member of
   * in the specified workspace.
   *
   * @param userId
   * @param workspaceId
   */
  private teamMembersWithCreateAccess = (userId: Id, workspaceId: Id) => {
    return this.teamMemberStore
      .teamMembersForWorkspaceMember(workspaceId, userId)
      .filter((tm) =>
        tm.hasSomePermissions('CREATE_TEAM_JOBS', 'START_TEAM_JOBS'),
      )
  }
}

/**
 * Params used to draft a new Job.
 */
interface DraftJobParams {
  teamId: Id | null
  ownerId: Id
  legendVersionId: LegendVersionId
}

/**
 * Result from `parseDraftParams`.
 */
type ParseDraftParamsResult = readonly [
  DraftJobParams | null,
  readonly DraftChange[],
]

// We don't currently have enough info.
const EMPTY_DRAFT_PARAMS: ParseDraftParamsResult = [null, [] as const] as const

/**
 * Gets draft params from changes.
 * @param changes
 * @returns A tuple of the draft creation params and the remaining
 *  changes that can be used to update afterwards. If the first element
 *  of the tuple is `null`, we don't currently have enough info to create
 *  a draft, which will cause the Persister to include those changes in the
 *  next attempt once more info has been collected.
 */
function parseDraftParams(changes: DraftChange[]): ParseDraftParamsResult {
  let ownerId: Id | undefined
  let legendVersionId: LegendVersionId | undefined
  let teamId: Id | null

  const postCreationChanges: DraftChange[] = []
  for (const change of changes) {
    switch (change.type) {
      // We don't actually use this one.
      case 'WorkspaceSelected':
        break
      case 'TeamSelected':
        teamId = change.team === TeamSelection.myself ? null : change.team?.id
        break
      case 'LegendSelected':
        legendVersionId = change.legend?.versionId
        break
      case 'OwnerSelected':
        ownerId = change.owner?.user.publicId
        break
      default:
        // Other changes that we want to persist
        // after having created the job.
        postCreationChanges.push(change)
    }
  }

  if (
    legendVersionId === undefined ||
    teamId === undefined ||
    ownerId === undefined
  ) {
    return EMPTY_DRAFT_PARAMS
  }

  const params: DraftJobParams = {
    teamId,
    ownerId,
    legendVersionId,
  }

  return [params, postCreationChanges]
}
